import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import { authHelpers, db } from '../../firebaseClient';
import { doc, getDoc } from 'firebase/firestore';
import {
  slugify,
  isSlugTaken,
  createBlogPost,
  updateBlogPost,
  getPostById,
  buildVersionSnapshot,
  appendToVersionHistory,
  autosaveDraft,
  generatePreviewToken,
} from '../../utils/blogHelpers';

// ---------------------------------------------------------------------------
// Config — fill these in via your .env (Vite: import.meta.env, CRA: process.env)
// ---------------------------------------------------------------------------
const BLOG_TOOLS_API = import.meta.env.VITE_BLOG_TOOLS_API || 'https://your-render-service.onrender.com';
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'your-cloud-name';
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'nesolearn_blog_covers';

const CATEGORIES = ['Thought Leadership', 'Case Study', 'Product', 'Company News', 'Guides'];
const SOURCE_PLATFORMS = ['LinkedIn', 'GitBook', 'Medium', 'Twitter / X', 'Other'];

const emptyForm = {
  sourceUrl: '',
  sourcePlatform: 'LinkedIn',
  pastedText: '',
  title: '',
  excerpt: '',
  contentBody: '',
  slug: '',
  category: CATEGORIES[0],
  type: 'article',
  coverImageUrl: '',
  coverImageMode: 'upload', // 'upload' | 'link'
  authorName: 'Nesolearn Team',
  authorRole: '',
  featured: false,
  previewToken: null,
};

const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    { method: 'POST', body: formData }
  );
  if (!response.ok) throw new Error('Cloudinary upload failed');
  const data = await response.json();
  return data.secure_url;
};

const BlogComposer = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = !!editId;

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugWarning, setSlugWarning] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successSlug, setSuccessSlug] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(isEditMode);

  const [uploading, setUploading] = useState(false);
  const [unfurling, setUnfurling] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [toolError, setToolError] = useState('');
  const fileInputRef = useRef(null);
  const originalSlugRef = useRef(null);
  const originalPostRef = useRef(null);
  const hydratedRef = useRef(false);
  const autosaveTimeoutRef = useRef(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState('');
  const [previewInfo, setPreviewInfo] = useState(null);
  const [previewCopied, setPreviewCopied] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleError, setScheduleError] = useState('');

  useEffect(() => {
    const unsubscribe = authHelpers.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        setIsAdmin(false);
        setCheckingAccess(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'platformAdmins', firebaseUser.uid));
        setIsAdmin(snap.exists());
      } catch (err) {
        console.error('[BlogComposer] Admin check failed:', err);
        setIsAdmin(false);
      }
      setCheckingAccess(false);
    });
    return () => unsubscribe();
  }, []);

  // Edit mode: once we know the user is admin, load the existing post
  // and pre-fill the form. slugEdited is set true immediately so the
  // slugify-on-title-change logic never overwrites the existing slug.
  useEffect(() => {
    if (!isEditMode || checkingAccess || !isAdmin) return;
    (async () => {
      const { post } = await getPostById(editId);
      if (post) {
        setForm((p) => ({
          ...p,
          sourceUrl: post.sourceUrl || '',
          sourcePlatform: post.sourcePlatform || 'LinkedIn',
          title: post.title || '',
          excerpt: post.excerpt || '',
          contentBody: post.type === 'quote' ? (post.quoteText || '') : (post.contentBody || ''),
          slug: post.slug || '',
          category: post.category || CATEGORIES[0],
          type: post.type || 'article',
          coverImageUrl: post.coverImageUrl || '',
          coverImageMode: 'link',
          authorName: post.author?.name || 'Nesolearn Team',
          authorRole: post.author?.role || '',
          featured: !!post.featured,
          previewToken: post.previewToken || null,
        }));
        setSlugEdited(true);
        originalSlugRef.current = post.slug || '';
        originalPostRef.current = post;
        setVersionHistory(Array.isArray(post.versionHistory) ? post.versionHistory : []);
        if (post.status === 'scheduled' && typeof post.scheduledFor === 'number') {
          const d = new Date(post.scheduledFor);
          const pad = (n) => String(n).padStart(2, '0');
          setScheduleDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
          setScheduleTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
          setScheduleOpen(true);
        }
      }
      setLoadingExisting(false);
    })();
  }, [isEditMode, checkingAccess, isAdmin, editId]);

  // --- Cover art: direct upload ---
  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setToolError('');
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      setForm((p) => ({ ...p, coverImageUrl: url }));
    } catch (err) {
      console.error(err);
      setToolError('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // --- Cover art + title/excerpt: auto-unfurl from a source URL ---
  const handleUnfurl = async () => {
    if (!form.sourceUrl.trim()) return;
    setToolError('');
    setUnfurling(true);
    try {
      const res = await fetch(`${BLOG_TOOLS_API}/api/unfurl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: form.sourceUrl.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unfurl failed');

      setForm((p) => ({
        ...p,
        title: p.title || data.title || '',
        excerpt: p.excerpt || data.description || '',
        coverImageUrl: data.image || p.coverImageUrl,
        coverImageMode: data.image ? 'link' : p.coverImageMode,
        slug: slugEdited ? p.slug : slugify(p.title || data.title || ''),
      }));
    } catch (err) {
      console.error(err);
      setToolError("Couldn't read that link's preview data. You can still fill in fields manually.");
    } finally {
      setUnfurling(false);
    }
  };

  // --- AI-powered smart suggestions from pasted text ---
  const handleAiSuggest = async () => {
    if (!form.pastedText.trim()) return;
    setToolError('');
    setAiSuggesting(true);
    try {
      const res = await fetch(`${BLOG_TOOLS_API}/api/ai-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: form.pastedText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI suggestion failed');

      setForm((p) => ({
        ...p,
        title: data.title || p.title,
        excerpt: data.excerpt || p.excerpt,
        contentBody: data.bodyHtml || p.contentBody,
        slug: slugEdited ? p.slug : slugify(data.title || p.title),
      }));
    } catch (err) {
      console.error(err);
      setToolError('AI suggestion failed. You can write the fields manually below.');
    } finally {
      setAiSuggesting(false);
    }
  };

  // --- Full-article fetch + AI summary, for public non-gated sources (GitBook, docs sites) ---
  const [fetchingFull, setFetchingFull] = useState(false);
  const handleFetchFullText = async () => {
    if (!form.sourceUrl.trim()) return;
    setToolError('');
    setFetchingFull(true);
    try {
      const fetchRes = await fetch(`${BLOG_TOOLS_API}/api/fetch-full-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: form.sourceUrl.trim() }),
      });
      const fetchData = await fetchRes.json();
      if (!fetchRes.ok) throw new Error(fetchData.error || 'Could not read that page');

      const aiRes = await fetch(`${BLOG_TOOLS_API}/api/ai-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: fetchData.text }),
      });
      const aiData = await aiRes.json();
      if (!aiRes.ok) throw new Error(aiData.error || 'AI summarization failed');

      setForm((p) => ({
        ...p,
        title: aiData.title || p.title,
        excerpt: aiData.excerpt || p.excerpt,
        contentBody: aiData.bodyHtml || p.contentBody,
        pastedText: p.pastedText || fetchData.text.slice(0, 2000),
        slug: slugEdited ? p.slug : slugify(aiData.title || p.title),
      }));
    } catch (err) {
      console.error(err);
      setToolError(err.message || "Couldn't read and summarize that page automatically. Try pasting the text manually instead.");
    } finally {
      setFetchingFull(false);
    }
  };

  const handleTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugEdited ? prev.slug : slugify(value),
    }));
  };

  const handleSlugCheck = async () => {
    if (!form.slug) return;
    const taken = await isSlugTaken(form.slug);
    setSlugWarning(taken ? 'This slug is already in use by another post.' : '');
  };

  const canSubmit = useMemo(
    () => form.title.trim() && form.slug.trim() && form.excerpt.trim() && !submitting,
    [form, submitting]
  );

  const handleSubmit = async (status) => {
    if (status === 'scheduled') {
      if (!scheduleDate || !scheduleTime) {
        setScheduleError('Pick both a date and a time.');
        return;
      }
      const scheduledForMs = new Date(`${scheduleDate}T${scheduleTime}`).getTime();
      if (Number.isNaN(scheduledForMs) || scheduledForMs <= Date.now()) {
        setScheduleError('Pick a date and time in the future.');
        return;
      }
      setScheduleError('');
    }

    setSubmitting(true);
    setSubmitError('');

    // Only block on a slug collision if the slug actually changed away
    // from what this post already has — otherwise editing a post always
    // "collides" with itself.
    if (!isEditMode || form.slug !== originalSlugRef.current) {
      const taken = await isSlugTaken(form.slug);
      if (taken) {
        setSlugWarning('This slug is already in use — please change it before publishing.');
        setSubmitting(false);
        return;
      }
    }

    const payload = {
      slug: form.slug,
      title: form.title,
      excerpt: form.excerpt,
      contentBody: form.type === 'quote' ? '' : form.contentBody,
      quoteText: form.type === 'quote' ? form.contentBody.replace(/<[^>]+>/g, '') : null,
      coverImageUrl: form.coverImageUrl || null,
      category: form.category,
      sourceUrl: form.sourceUrl || null,
      sourcePlatform: form.sourceUrl ? form.sourcePlatform : null,
      type: form.type,
      author: {
        name: form.authorName || 'Nesolearn Team',
        role: form.authorRole || '',
        avatarUrl: '',
      },
      featured: form.featured,
      status,
      scheduledFor: status === 'scheduled'
        ? new Date(`${scheduleDate}T${scheduleTime}`).getTime()
        : null,
      previewToken: form.previewToken || generatePreviewToken(),
    };

    if (isEditMode && originalPostRef.current) {
      payload.versionHistory = appendToVersionHistory(
        originalPostRef.current.versionHistory,
        buildVersionSnapshot(originalPostRef.current)
      );
    }

    let resultId = editId;
    let error;
    if (isEditMode) {
      ({ error } = await updateBlogPost(editId, payload));
    } else {
      const created = await createBlogPost(payload);
      error = created.error;
      resultId = created.id;
    }

    setSubmitting(false);

    if (error) {
      setSubmitError(
        isEditMode
          ? 'Something went wrong saving your changes. Please try again.'
          : 'Something went wrong publishing this post. Please try again.'
      );
      return;
    }

    setSuccessSlug(form.slug);
    setPreviewInfo({ id: resultId, token: payload.previewToken });
    setPreviewCopied(false);
    if (!isEditMode) {
      setForm(emptyForm);
      setSlugEdited(false);
    } else {
      originalPostRef.current = { ...originalPostRef.current, ...payload };
      setVersionHistory(payload.versionHistory || []);
      if (status !== 'scheduled') setScheduleOpen(false);
    }
  };

  const handleCopyPreviewLink = async (info) => {
    const target = info || previewInfo;
    if (!target) return;
    const url = `${window.location.origin}/blog/preview/${target.id}?token=${target.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setPreviewCopied(true);
      setTimeout(() => setPreviewCopied(false), 2000);
    } catch (err) {
      console.error('[BlogComposer] Could not copy preview link:', err);
    }
  };

  // Silent autosave — only runs for an existing post that's already
  // loaded, debounced so it doesn't fire on every keystroke, and skips
  // the very first change (which is just hydration from getPostById).
  useEffect(() => {
    if (!isEditMode || !editId || loadingExisting) return;
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    setAutosaveStatus('');
    autosaveTimeoutRef.current = setTimeout(async () => {
      setAutosaveStatus('saving');
      const { error: autosaveErr } = await autosaveDraft(editId, {
        title: form.title,
        excerpt: form.excerpt,
        contentBody: form.type === 'quote' ? '' : form.contentBody,
        quoteText: form.type === 'quote' ? form.contentBody.replace(/<[^>]+>/g, '') : null,
      });
      setAutosaveStatus(autosaveErr ? '' : 'saved');
    }, 2500);
    return () => {
      if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    };
  }, [form.title, form.excerpt, form.contentBody, form.type, isEditMode, editId, loadingExisting]);

  const handleRestoreVersion = (version) => {
    if (!window.confirm("Restore this version into the form? Your current unsaved edits will be replaced — you'll still need to Save/Publish to apply it.")) return;
    setForm((p) => ({
      ...p,
      title: version.title || '',
      excerpt: version.excerpt || '',
      contentBody: version.type === 'quote' ? (version.quoteText || '') : (version.contentBody || ''),
      type: version.type || 'article',
    }));
    setHistoryOpen(false);
  };

  const formatVersionDate = (ms) => {
    if (!ms) return '';
    return new Date(ms).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-white">
        <p className="text-muted-foreground text-sm">Checking access…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/home-landing-page" replace />;
  }

  if (loadingExisting) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-white">
        <p className="text-muted-foreground text-sm">Loading post…</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? 'Edit Post' : 'Blog Composer'} | Nesolearn Admin</title>
      </Helmet>

      <div className="min-h-screen w-full overflow-x-hidden bg-white">
        <NavigationBar themed />

        <main className="pt-28 pb-20 max-w-6xl mx-auto px-4 md:px-8">
          <Link to="/blog-admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4">
            <Icon name="ArrowLeft" size={14} />
            Back to Post Manager
          </Link>
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {isEditMode ? 'Edit Post' : 'Blog Composer'}
          </h1>
          <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
            <p className="text-muted-foreground text-sm">
              {isEditMode
                ? 'Update this post\'s fields, then save.'
                : 'Paste a source link and/or post text, let it suggest the fields, then review and publish.'}
            </p>
            {isEditMode && (
              <div className="flex items-center gap-3">
                {autosaveStatus === 'saving' && (
                  <span className="text-xs text-muted-foreground">Saving…</span>
                )}
                {autosaveStatus === 'saved' && (
                  <span className="text-xs text-muted-foreground">Autosaved</span>
                )}
                {form.previewToken && (
                  <button
                    type="button"
                    onClick={() => {
                      const info = { id: editId, token: form.previewToken };
                      setPreviewInfo(info);
                      handleCopyPreviewLink(info);
                    }}
                    className="text-xs font-medium text-foreground underline underline-offset-2"
                  >
                    {previewCopied ? 'Copied!' : 'Copy preview link'}
                  </button>
                )}
                {versionHistory.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    className="text-xs font-medium text-foreground underline underline-offset-2"
                  >
                    Version history ({versionHistory.length})
                  </button>
                )}
              </div>
            )}
          </div>

          {isEditMode && historyOpen && versionHistory.length > 0 && (
            <div className="mb-8 border border-border rounded-lg divide-y divide-border max-h-64 overflow-y-auto">
              {[...versionHistory].reverse().map((v, i) => (
                <div key={v.savedAt || i} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground line-clamp-1">{v.title || 'Untitled'}</p>
                    <p className="text-xs text-muted-foreground">{formatVersionDate(v.savedAt)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRestoreVersion(v)}
                    className="shrink-0 px-3 py-1.5 rounded-full border border-input text-xs font-medium hover:bg-muted transition-smooth"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}

          {successSlug && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <span>{isEditMode ? 'Changes saved.' : 'Post saved.'}</span>
              <div className="flex items-center gap-4">
                {previewInfo && (
                  <button type="button" onClick={() => handleCopyPreviewLink()} className="underline font-medium">
                    {previewCopied ? 'Preview link copied!' : 'Copy preview link'}
                  </button>
                )}
                <Link to={`/blog/${successSlug}`} className="underline font-medium">
                  View it →
                </Link>
                {isEditMode && (
                  <Link to="/blog-admin" className="underline font-medium">
                    Back to manager →
                  </Link>
                )}
              </div>
            </div>
          )}
          {submitError && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {submitError}
            </div>
          )}
          {toolError && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {toolError}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: form */}
            <div className="space-y-6">
              {/* Source link + platform */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Source URL</label>
                <div className="flex gap-2">
                  <select
                    value={form.sourcePlatform}
                    onChange={(e) => setForm((p) => ({ ...p, sourcePlatform: e.target.value }))}
                    className="h-10 px-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {SOURCE_PLATFORMS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={form.sourceUrl}
                    onChange={(e) => setForm((p) => ({ ...p, sourceUrl: e.target.value }))}
                    placeholder="https://..."
                    className="flex-1 h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleUnfurl}
                  disabled={!form.sourceUrl.trim() || unfurling}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-input text-sm font-medium disabled:opacity-40 hover:bg-muted transition-smooth"
                >
                  <Icon name="Link2" size={14} />
                  {unfurling ? 'Reading link…' : 'Auto-fill cover image, title & excerpt from link'}
                </button>
                {form.sourcePlatform !== 'LinkedIn' && form.sourcePlatform !== 'Twitter / X' && (
                  <button
                    type="button"
                    onClick={handleFetchFullText}
                    disabled={!form.sourceUrl.trim() || fetchingFull}
                    className="mt-2 ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black text-white text-sm font-medium disabled:opacity-40 transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105 disabled:hover:scale-100"
                  >
                    <Icon name="Sparkles" size={14} />
                    {fetchingFull ? 'Reading full article…' : 'Fetch & summarize full article'}
                  </button>
                )}
                {(form.sourcePlatform === 'LinkedIn' || form.sourcePlatform === 'Twitter / X') && (
                  <p className="text-xs text-amber-700 mt-1">
                    {form.sourcePlatform} hides full post text from public pages — paste the post text below for a real AI summary instead of relying on the link alone.
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Becomes the "Read full article on {form.sourcePlatform}" button on the article page.
                </p>
              </div>

              <hr className="border-border" />

              {/* Cover art: upload OR link, admin's choice */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Cover image</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, coverImageMode: 'upload' }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                      form.coverImageMode === 'upload'
                        ? 'bg-black text-white border-black'
                        : 'border-input text-foreground hover:bg-muted'
                    }`}
                  >
                    Upload a file
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, coverImageMode: 'link' }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                      form.coverImageMode === 'link'
                        ? 'bg-black text-white border-black'
                        : 'border-input text-foreground hover:bg-muted'
                    }`}
                  >
                    Paste an image URL
                  </button>
                </div>

                {form.coverImageMode === 'upload' ? (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelected}
                      className="text-sm"
                    />
                    {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading…</p>}
                  </div>
                ) : (
                  <input
                    type="url"
                    value={form.coverImageUrl}
                    onChange={(e) => setForm((p) => ({ ...p, coverImageUrl: e.target.value }))}
                    placeholder="https://..."
                    className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                )}

                {form.coverImageUrl && (
                  <img
                    src={form.coverImageUrl}
                    alt="Cover preview"
                    className="mt-3 w-full aspect-video object-cover rounded-lg border border-border"
                  />
                )}
              </div>

              <hr className="border-border" />

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Paste the post text here
                </label>
                <textarea
                  value={form.pastedText}
                  onChange={(e) => setForm((p) => ({ ...p, pastedText: e.target.value }))}
                  rows={6}
                  placeholder="Paste the full text of the post..."
                  className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={handleAiSuggest}
                  disabled={!form.pastedText.trim() || aiSuggesting}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black text-white text-sm font-medium disabled:opacity-40 transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105 disabled:hover:scale-100"
                >
                  <Icon name="Sparkles" size={14} />
                  {aiSuggesting ? 'Thinking…' : 'AI: suggest title, excerpt & body'}
                </button>
              </div>

              <hr className="border-border" />

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugEdited(true);
                    setForm((p) => ({ ...p, slug: slugify(e.target.value) }));
                  }}
                  onBlur={handleSlugCheck}
                  className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                />
                {slugWarning && <p className="text-xs text-red-600 mt-1">{slugWarning}</p>}
                <p className="text-xs text-muted-foreground mt-1">
                  Will be available at /blog/{form.slug || 'your-slug'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {form.type === 'quote' ? 'Quote text' : 'Body content'}
                </label>
                <textarea
                  value={form.contentBody}
                  onChange={(e) => setForm((p) => ({ ...p, contentBody: e.target.value }))}
                  rows={8}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                    className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="article">Article</option>
                    <option value="quote">Quote</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Author name</label>
                  <input
                    type="text"
                    value={form.authorName}
                    onChange={(e) => setForm((p) => ({ ...p, authorName: e.target.value }))}
                    className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Author role</label>
                  <input
                    type="text"
                    value={form.authorRole}
                    onChange={(e) => setForm((p) => ({ ...p, authorRole: e.target.value }))}
                    placeholder="Founder & CEO"
                    className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm((p) => ({ ...p, featured: e.target.checked }))}
                />
                Feature on the home page
              </label>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={() => handleSubmit('published')}
                  className="px-5 py-2.5 rounded-full bg-black text-white text-sm font-medium disabled:opacity-40 transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105 disabled:hover:scale-100"
                >
                  {isEditMode ? 'Save & publish' : 'Publish'}
                </button>
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={() => handleSubmit('draft')}
                  className="px-5 py-2.5 rounded-full border border-input text-sm font-medium disabled:opacity-40 hover:bg-muted transition-smooth"
                >
                  {isEditMode ? 'Save as draft' : 'Save as draft'}
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleOpen((v) => !v)}
                  className="px-5 py-2.5 rounded-full border border-input text-sm font-medium hover:bg-muted transition-smooth"
                >
                  {scheduleOpen ? 'Cancel scheduling' : 'Schedule for later'}
                </button>
              </div>

              {scheduleOpen && (
                <div className="mt-2 border border-border rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Date</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Time</label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-full h-10 px-3 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>
                  {scheduleError && <p className="text-xs text-red-600">{scheduleError}</p>}
                  <p className="text-xs text-muted-foreground">
                    Uses your browser's local time zone. The post publishes itself the next time anyone loads the blog after this time.
                  </p>
                  <button
                    type="button"
                    disabled={!canSubmit}
                    onClick={() => handleSubmit('scheduled')}
                    className="px-5 py-2.5 rounded-full bg-black text-white text-sm font-medium disabled:opacity-40 transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105 disabled:hover:scale-100"
                  >
                    Confirm schedule
                  </button>
                </div>
              )}
            </div>

            {/* Right: live preview */}
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Preview</p>
              <div className="rounded-lg border border-border p-2">
                <div className="w-full aspect-video rounded-lg bg-muted overflow-hidden">
                  {form.coverImageUrl ? (
                    <img src={form.coverImageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                      No cover image
                    </div>
                  )}
                </div>
                <div className="space-y-2 px-2 pb-2 pt-3">
                  <div className="text-muted-foreground flex items-center gap-2 text-[11px] sm:text-xs">
                    <p>by {form.authorName || 'Nesolearn Team'}</p>
                    <div className="bg-muted-foreground size-1 rounded-full" />
                    <p>{form.category}</p>
                  </div>
                  <h2 className="line-clamp-2 text-lg leading-5 font-semibold tracking-tight">
                    {form.title || 'Untitled post'}
                  </h2>
                  <p className="text-muted-foreground line-clamp-3 text-sm">
                    {form.excerpt || 'Excerpt will appear here.'}
                  </p>
                </div>
              </div>

              {form.sourceUrl && (
                <div className="mt-4">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white text-xs font-medium">
                    Read full article on {form.sourcePlatform}
                    <Icon name="ExternalLink" size={12} />
                  </span>
                </div>
              )}

              {form.type !== 'quote' && form.contentBody && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-foreground mb-2">Body preview</p>
                  <div
                    className="prose prose-sm prose-neutral max-w-none border border-border rounded-lg p-4"
                    dangerouslySetInnerHTML={{ __html: form.contentBody }}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default BlogComposer;